import { useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import clsx from 'clsx'

import { useAuth, deleteAuthData } from '../hooks/useAuth'

const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    criteriaMode: 'all',
  })
  const { signin } = useAuth()
  let location = useLocation()
  const from = location.state?.from?.pathname ?? '/'
  let navigate = useNavigate()
  const [formMessage, setFormMessage] = useState({})
  const [loading, setLoading] = useState(false)

  const onFormSubmit = async ({ email, password }) => {
    try {
      setLoading(true)
      await signin(email, password, setFormMessage, () => {
        navigate(from, { replace: true })
      })
    } catch (err) {
      console.error('onFormSubmit :: error', err)

      if (
        err.message === 'incorrect_password' ||
        err.message === 'invalid_username' ||
        err.message === 'invalid_email'
      ) {
        setFormMessage({ type: 'error', text: 'Incorrect email/password!' })
      } else {
        setFormMessage({
          type: 'error',
          text: 'Unable to log in! Please try again.',
        })
      }

      deleteAuthData()
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="min-h-[calc(100vh-200px)]">
      <div className="container pb-20 pt-2">
        <h1 className="mb-10 text-center text-4xl font-bold text-skin-accent">
          log in.
        </h1>
        <div className="mx-auto flex min-h-[437px] w-[315px] flex-col items-center divide-y divide-light-gray rounded-[19px] bg-white py-2 pb-10 shadow-lg [&>*]:px-6">
          {/** Header */}
          <div className="py-4 text-sm font-bold text-flame">
            Welcome to TMC!
          </div>

          <div className="w-full text-center">
            {/** Register button */}
            <NavLink
              to="/register"
              className="my-12 block w-full rounded-full border-2 border-flame px-6 pb-3 pt-[0.6rem] font-bold text-flame transition-colors hover:bg-skin-button-accent hover:text-white active:translate-y-[0.75px] active:scale-[0.99]"
            >
              Click to Register!
            </NavLink>
            <div className="text-center text-sm text-dark-charcoal">
              Already have an account?
            </div>
            <form className="mb-4 mt-6" onSubmit={handleSubmit(onFormSubmit)}>
              {/** Email input */}
              <fieldset className="mb-5">
                <input
                  {...register('email', {
                    required: true,
                    pattern: /^\S+@\S+$/i,
                  })}
                  name="email"
                  type="email"
                  placeholder="Email"
                  className={clsx(
                    'w-full rounded-[6.5px] placeholder-dark-gray',
                    errors.email ? 'border-red-500' : 'border-light-gray'
                  )}
                />
                {errors.email && (
                  <p className="text-left text-xs text-red-500">
                    {errors.email.type === 'required' && 'This is required'}
                    {errors.email.type === 'pattern' &&
                      'This email address is invalid'}
                  </p>
                )}
              </fieldset>

              {/** Password input */}
              <fieldset className="mb-5">
                <input
                  {...register('password', { required: true })}
                  name="password"
                  type="password"
                  placeholder="Password"
                  className={clsx(
                    'w-full rounded-[6.5px] placeholder-dark-gray',
                    errors.password ? 'border-red-500' : 'border-light-gray'
                  )}
                />
                {errors.password && (
                  <p className="text-left text-xs text-red-500">
                    {errors.password.type === 'required' && 'This is required'}
                    {errors.password.type === 'pattern' &&
                      'This password is invalid'}
                  </p>
                )}
              </fieldset>

              {formMessage?.text && (
                <p
                  className={clsx(
                    'my-5 text-xs',
                    formMessage?.type === 'error' && 'text-red-500'
                  )}
                >
                  {formMessage.text}
                </p>
              )}

              {/** Login button */}
              <input
                type="submit"
                value={loading ? 'please wait...' : 'log in'}
                disabled={loading}
                className={clsx(
                  'w-full rounded-full bg-skin-button-accent px-6 pb-3 pt-[0.6rem] font-bold text-white transition-colors',
                  loading
                    ? 'opacity-50'
                    : 'hover:bg-skin-button-accent-hover active:translate-y-[0.75px] active:scale-[0.99]'
                )}
              />
            </form>

            {/** Forgot password link */}
            <NavLink
              to="/forgot"
              className="text-xs text-silver-chalice hover:underline"
            >
              Forgot your password?
            </NavLink>
          </div>
        </div>
      </div>
    </main>
  )
}

export default Login
